import React from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid2,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  MenuBook as MenuBookIcon,
  Casino as CasinoIcon,
  Create as CreateIcon,
  ManageAccounts as ManageIcon,
  Group as GroupIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

function HostGuide() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const steps = [
    {
      label: 'Get Host Access',
      description: 'Obtain your host access code and register your account',
      icon: <SecurityIcon />,
      content: [
        'Contact LocalPokerContact@gmail.com to request a host access code',
        'Go to Settings > Host Registration and enter your access code',
        'Accept the Host Terms of Service',
        'Your account will be upgraded with host privileges'
      ]
    },
    {
      label: 'Create Your First Game',
      description: 'Set up a new poker game with all the necessary details',
      icon: <CreateIcon />,
      content: [
        'Navigate to Host Panel > Create Game',
        'Choose between Cash Game or Tournament format',
        'Set your game name, stakes, and buy-in amounts',
        'Configure game timing (start time, duration)',
        'Add location details (address will be shared with accepted players)',
        'Set access level (Public or Private with access code)',
        'Review and publish your game'
      ]
    },
    {
      label: 'Manage Player Requests',
      description: 'Review and approve players who want to join your games',
      icon: <ManageIcon />,
      content: [
        'Monitor the "All Requests" section in your Host Panel',
        'Review player profiles and histories before approving',
        'Accept or decline requests based on your criteria',
        'Use your whitelist/blacklist to manage preferred players',
        'Communicate with players through the messaging system'
      ]
    },
    {
      label: 'Game Day Management',
      description: 'Execute and manage your live poker game',
      icon: <CasinoIcon />,
      content: [
        'Start your game when players arrive',
        'Monitor current players and waitlist',
        'Handle late arrivals and player substitutions',
        'Manage any disputes or issues that arise',
        'End the game when finished and process payouts'
      ]
    }
  ];

  const bestPractices = [
    {
      title: 'Game Setup',
      icon: <ScheduleIcon />,
      tips: [
        'Set realistic start times and stick to them',
        'Clearly communicate house rules and rake structure',
        'Ensure you have adequate seating and equipment',
        'Plan for food, drinks, and bathroom breaks'
      ]
    },
    {
      title: 'Player Management',
      icon: <GroupIcon />,
      tips: [
        'Screen new players carefully before approving',
        'Maintain a good mix of skill levels for fun games',
        'Build a regular player base for consistent games',
        'Handle conflicts professionally and fairly'
      ]
    },
    {
      title: 'Safety & Security',
      icon: <SecurityIcon />,
      tips: [
        'Only share your address with approved players',
        'Consider requiring deposits for commitment',
        'Keep detailed records of all transactions',
        'Ensure your games comply with local laws'
      ]
    },
    {
      title: 'Communication',
      icon: <NotificationsIcon />,
      tips: [
        'Send reminders 24 hours before games',
        'Update players about any changes promptly',
        'Use the built-in messaging system for privacy',
        'Maintain professional communication at all times'
      ]
    }
  ];

  const gameTypes = [
    {
      type: 'Cash Games',
      description: 'Players buy chips and can leave at any time',
      features: [
        'Flexible entry and exit times',
        'Stakes remain constant throughout',
        'Good for regular weekly games',
        'Easy to manage payouts'
      ]
    },
    {
      type: 'Tournaments',
      description: 'Structured competition with elimination format',
      features: [
        'Fixed buy-in with prize pool distribution',
        'Escalating blinds structure',
        'Defined start and end times',
        'More exciting for competitive players'
      ]
    }
  ];

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            color: 'primary.main',
            fontWeight: 600
          }}
        >
          <MenuBookIcon color="primary" fontSize={isMobile ? "large" : "inherit"} />
          Host Guide
        </Typography>
        <Typography 
          variant="body1" 
          color="textSecondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Complete guide to creating and managing successful poker games
        </Typography>
      </Box>

      <Container maxWidth="lg">
        {/* Welcome Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Welcome to Hosting!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              Hosting poker games on Local Poker connects you with players in your area and helps build 
              a thriving local poker community. This guide will walk you through everything you need to 
              know to create and manage successful games.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>New to hosting?</strong> Start with smaller cash games to get familiar with the platform 
                before hosting larger tournaments.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Step-by-Step Process */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              Getting Started: Step-by-Step
            </Typography>
            
            <Stepper orientation={isMobile ? "vertical" : "vertical"} sx={{ mt: 2 }}>
              {steps.map((step, index) => (
                <Step key={index} active={true}>
                  <StepLabel
                    icon={
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText'
                      }}>
                        {React.cloneElement(step.icon, { fontSize: 'small' })}
                      </Box>
                    }
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <List dense>
                      {step.content.map((item, itemIndex) => (
                        <ListItem key={itemIndex} sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Game Types */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              Game Format Options
            </Typography>
            
            <Grid2 container spacing={3} sx={{ justifyContent: 'center' }}>
              {gameTypes.map((gameType, index) => (
                <Grid2 xs={12} md={5} key={index}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, mx: 'auto', maxWidth: 400 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {gameType.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {gameType.description}
                      </Typography>
                      <List dense>
                        {gameType.features.map((feature, featureIndex) => (
                          <ListItem key={featureIndex} sx={{ pl: 0, textAlign: 'left' }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <InfoIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={feature}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </CardContent>
        </Card>

        {/* Best Practices */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              Best Practices for Successful Hosting
            </Typography>
            
            <Grid2 container spacing={3} sx={{ justifyContent: 'center' }}>
              {bestPractices.map((practice, index) => (
                <Grid2 xs={12} md={6} key={index}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, mx: 'auto', maxWidth: 450 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
                        {React.cloneElement(practice.icon, { color: 'primary' })}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {practice.title}
                        </Typography>
                      </Box>
                      <List dense>
                        {practice.tips.map((tip, tipIndex) => (
                          <ListItem key={tipIndex} sx={{ pl: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={tip}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </CardContent>
        </Card>

        {/* Important Reminders */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              Important Reminders
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Legal Compliance:</strong> Ensure your games comply with local and state gambling laws. 
                You are responsible for understanding and following all applicable regulations.
              </Typography>
            </Alert>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Financial Responsibility:</strong> You are solely responsible for all money handling, 
                payouts, and any disputes that arise during your games.
              </Typography>
            </Alert>
            
            <Alert severity="success">
              <Typography variant="body2">
                <strong>Community Building:</strong> Focus on creating a fun, welcoming environment that encourages 
                players to return and bring friends to grow the local poker community.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default HostGuide;